import { supabase } from '@/integrations/supabase/client';

/**
 * Generate a URL-friendly slug from a title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Check if user has at least one verified Metefy token
 */
export async function userHasVerifiedTokens(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('metefy_tokens')
      .select('id')
      .eq('user_id', userId)
      .eq('is_verified', true)
      .limit(1);
    
    if (error) {
      console.error('Error checking tokens:', error);
      return false;
    }
    
    return (data?.length ?? 0) > 0;
  } catch (error) {
    console.error('Error checking tokens:', error);
    return false;
  }
}

/**
 * Check if a slug is already taken
 */
export async function isSlugTaken(slug: string, excludeId?: string): Promise<boolean> {
  try {
    let query = supabase
      .from('user_blogs')
      .select('id')
      .eq('slug', slug)
      .limit(1);
    
    if (excludeId) {
      query = query.neq('id', excludeId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error checking slug:', error);
      return false;
    }
    
    return (data?.length ?? 0) > 0;
  } catch (error) {
    console.error('Error checking slug:', error);
    return false;
  }
}

/**
 * Generate a unique slug by appending a number if needed
 */
export async function generateUniqueSlug(title: string, excludeId?: string): Promise<string> {
  let baseSlug = generateSlug(title);
  let slug = baseSlug;
  let counter = 1;
  
  while (await isSlugTaken(slug, excludeId)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
  
  return slug;
}

/**
 * Generate an excerpt from HTML content
 */
export function generateExcerpt(content: any): string {
  if (!content) return '';
  
  // Helper to extract text from Tiptap JSON
  const extractText = (node: any): string => {
    if (typeof node === 'string') return node;
    if (node.type === 'text') return node.text || '';
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractText).join(' ');
    }
    return '';
  };
  
  const text = extractText(content).trim();
  // Get first 160 characters, trying to cut at a whole word
  if (text.length <= 160) return text;
  return text.substring(0, 160).replace(/\s+\S*$/, '') + '...';
}

